import { DatePicker, Form, Input, Modal, notification, Select, Space } from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import CustomButton from 'components/custom-button';
import { TYPE_JOB } from 'constants/common';
import { REQUIRED } from 'constants/rule-form';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import styles from '../styles.module.scss';
import { JOB_TYPE_OPTIONS } from '../labels';

interface DataPost {
  calendarDate?: string;
  title: string;
  content: string;
  type: any;
  group: string;
}

interface Props {
  isOpen: boolean;
  handleCancelCreateEditModal: () => void;
  onChangeStatusUpdateData: () => void;
  valueUserSelected: any;
}

// eslint-disable-next-line arrow-body-style
// Can not select days before today and today
const disabledDate: RangePickerProps['disabledDate'] = (current) => {
  return current && current.add(1, 'day') < dayjs().endOf('day');
};

const disabledDateTime = () => ({
  // disabledHours: () => range(0, 24).splice(0, moment().hours() + 1),
  // disabledMinutes: () => range(0, 60),
  // disabledSeconds: () => [55, 56],
});

const SUBMIT_TEXT: Record<string, string> = {
  [TYPE_JOB.NOW]: 'Gửi ngay',
  [TYPE_JOB.CALENDAR]: 'Lưu lịch gửi',
  [TYPE_JOB.NO]: 'Lưu',
};

const ModalCreateLiveShow = ({
  isOpen,
  valueUserSelected,
  handleCancelCreateEditModal,
  onChangeStatusUpdateData,
}: Props) => {
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const type = Form.useWatch('type', { form, preserve: true });
  const previewContent = Form.useWatch('content', form);
  const selectedGroups = Form.useWatch('group', form);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    const fetchGroup = async () => {
      try {
        const res: any = await AxiosUtils.get(API.getListGroup);
        setGroups(res?.data);
      } catch (error) {
        // Không tải được danh sách nhóm: ô chọn nhóm để trống, không chặn cả modal.
      }
    };

    fetchGroup();
  }, []);

  const onFinish = async (values: DataPost) => {
    const newValues = { ...values };

    if (newValues.calendarDate) {
      newValues.calendarDate = dayjs(newValues.calendarDate).format();
    }

    newValues.group = newValues.group.toString();

    setLoading(true);
    try {
      await AxiosUtils.post(API.createJob, { ...newValues });
      handleCancelCreateEditModal();
      onChangeStatusUpdateData();
      notification.success({
        message: 'Thành công',
        description: 'Tạo thành công!',
        duration: 4,
      });
    } catch (error) {
      notification.error({
        message: 'Không thành công',
        description: 'Có lỗi xảy ra, vui lòng thử lại.',
        duration: 4,
      });
    } finally {
      setLoading(false);
    }
  };

  const groupOptions = (groups || []).map((group: any) => ({
    value: `${group.id}`,
    label: group?.title || 'HP',
  }));
  const groupCount = Array.isArray(selectedGroups) ? selectedGroups.length : 0;

  return (
    <>
      <Modal
        forceRender
        open={isOpen}
        title={'Thêm tin nhắn tự động'}
        onCancel={handleCancelCreateEditModal}
        width={800}
        getContainer={false}
        footer={false}
      >
        <Form name="form" form={form} layout="vertical" onFinish={onFinish} autoComplete="off">
          <Form.Item
            label="Tiêu đề"
            extra="Chỉ để nhận biết trong danh sách; trong nhóm chat chỉ hiện phần Nội dung."
            name="title"
            rules={[
              REQUIRED,
              {
                max: 200,
                message: 'Không được quá 200 kí tự',
              },
            ]}
          >
            <Input placeholder="Nhập tiêu đề" />
          </Form.Item>

          <Form.Item
            label="Nội dung tin nhắn"
            rules={[{ required: true, message: 'Nội dung này bắt buộc' }]}
            name="content"
          >
            <Input.TextArea placeholder="Nhập nội dung" showCount maxLength={300} rows={4} />
          </Form.Item>

          <Form.Item label="Gửi vào nhóm chat" rules={[REQUIRED]} name="group">
            <Select
              mode="multiple"
              placeholder="Chọn một hoặc nhiều nhóm"
              options={groupOptions}
              optionFilterProp="label"
            />
          </Form.Item>

          <Form.Item
            label="Cách gửi"
            extra="Gửi ngay: gửi khi bấm nút bên dưới · Đặt lịch: tự gửi vào thời điểm đã chọn · Gửi thủ công: chỉ lưu lại, bấm “Gửi” trong danh sách khi cần."
            rules={[REQUIRED]}
            name="type"
          >
            <Select placeholder="Chọn cách gửi" options={JOB_TYPE_OPTIONS} />
          </Form.Item>

          {type === TYPE_JOB.CALENDAR && (
            <Form.Item
              label="Thời điểm gửi"
              extra="Hệ thống kiểm tra mỗi phút; tin có thể được gửi sớm hơn giờ đã chọn vài phút."
              rules={[REQUIRED]}
              name="calendarDate"
            >
              <DatePicker
                format="YYYY-MM-DD HH:mm"
                disabledDate={disabledDate}
                disabledTime={disabledDateTime}
                showTime={{ defaultValue: dayjs('00:00:00', 'HH:mm') }}
              />
            </Form.Item>
          )}

          <div className={styles.previewLabel}>
            Xem trước trong nhóm chat{groupCount > 0 ? ` (${groupCount} nhóm)` : ''}
          </div>
          <div className={styles.chatPreview}>
            <div className={styles.chatAuthor}>Tài khoản quản trị</div>
            <div className={styles.chatBubble}>
              {previewContent || 'Nội dung tin nhắn sẽ hiện ở đây.'}
            </div>
          </div>

          <div className="d-flex justify-center">
            <Space size="large">
              <CustomButton
                bgColor="secondary-green"
                text={SUBMIT_TEXT[type] || 'Lưu'}
                htmlType="submit"
                loading={loading}
              />
              <CustomButton
                text="Hủy"
                bgColor="primary-grey"
                htmlType="button"
                onClick={handleCancelCreateEditModal}
              />
            </Space>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default ModalCreateLiveShow;
